import { useEffect, useMemo, useState } from 'react';
import { Layout } from '../components/Layout';
import { Orb } from '../components/Orb';
import { api } from '../lib/api';
import { useAssetUrl } from '../lib/useAssetUrl';
import { AGENT_LABELS } from '../lib/boardMeta';
import { passLabel } from '../lib/runLabels';
import type { AgentId, DashboardStats, Me, Run, RunnerStatus, SafeModeState, TeamFinding } from '@shared/types';

/* ---------- formatters ---------- */
const nfCompact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const nfInt = new Intl.NumberFormat('en-US');
const fmtInt = (n: number) => nfInt.format(Math.round(n || 0));
const money = (n: number) => {
  const v = n || 0;
  const d = v !== 0 && Math.abs(v) < 1 ? 4 : 2;
  return '$' + v.toFixed(d);
};
const pct = (n: number) => ((n || 0) <= 1 ? (n || 0) * 100 : n).toFixed(1) + '%';
const hhmm = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};
const ago = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms) || ms < 0) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/* ---------- runner status line ----------
   Mac-mini runner presence from GET /admin/runner, polled every 30s.
   online = heartbeat within 15 min (the Worker decides). Fails soft —
   the line simply hides if the endpoint isn't reachable yet. */
function RunnerLine() {
  const [runner, setRunner] = useState<RunnerStatus | null>(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get<RunnerStatus>('/admin/runner');
        if (alive) setRunner(r);
      } catch {
        if (alive) setRunner(null);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (!runner) return null;
  const color = runner.online ? 'var(--mint)' : runner.last_heartbeat ? 'var(--coral)' : 'var(--faint)';
  const label = runner.online ? 'RUNNER ONLINE' : runner.last_heartbeat ? 'RUNNER OFFLINE' : 'RUNNER · NEVER SEEN';
  return (
    <div className="mono" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, letterSpacing: '.08em', color: 'var(--dim)', whiteSpace: 'nowrap' }}>
      <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: color, boxShadow: `0 0 9px ${color}`, flexShrink: 0 }} />
      <span style={{ color }}>{label}</span>
      {runner.last_heartbeat && <span className="faint">· last seen {ago(runner.last_heartbeat)}</span>}
    </div>
  );
}

/* ---------- real sparkline ----------
   Plots the actual last-14-day daily series for this metric (from
   DashboardStats.series) — real receipts, not decorative texture. Normalised to
   the series' own min/max; a flat/empty series renders an honest flat baseline. */
function buildSpark(values: number[]) {
  const n = values.length;
  if (n < 2) return null; // nothing real to draw yet
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const norm = (v: number) => (span > 0 ? (v - min) / span : 0.5); // flat → mid-line
  const pts = values.map((v, i) => `${((i / (n - 1)) * 100).toFixed(1)},${(34 - norm(v) * 30).toFixed(1)}`);
  const line = pts.join(' ');
  return { line, area: `0,36 ${line} 100,36` };
}

function Spark({ values }: { values: number[] }) {
  const built = useMemo(() => buildSpark(values), [values]);
  if (!built) return <div className="spark" aria-hidden="true" />;
  return (
    <svg className="spark" viewBox="0 0 100 36" preserveAspectRatio="none" role="img" aria-label="14-day trend">
      <polyline className="fill" points={built.area} />
      <polyline points={built.line} />
    </svg>
  );
}

function Stat({ label, value, values, sub }: { label: string; value: string; values: number[]; sub?: string }) {
  return (
    <div className="card stat">
      <p className="t-label">{label}</p>
      <div className="num mono">{value}</div>
      {sub && <p className="receipt" style={{ marginTop: 2 }}>{sub}</p>}
      <Spark values={values} />
    </div>
  );
}

/* ---------- safe-mode pulse ---------- */
function SafeMode({ s }: { s: SafeModeState }) {
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <p className="t-label">Safe Mode</p>
        <span className={`chip ${s.on ? 'chip-mint' : 'chip-coral'}`}>
          <i />
          {s.on ? 'ON · GUARDS LIVE' : 'OFF'}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 16 }}>
        <span className="t-gauge" style={{ fontSize: 44, color: 'var(--text)' }}>
          {fmtInt(s.blocked_count)}
        </span>
        <span className="t-label" style={{ letterSpacing: '.18em' }}>
          unsafe attempts blocked · loudly
        </span>
      </div>
      <div className="guards">
        {s.guards.length ? (
          s.guards.map((g) => (
            <span key={g.key} className={`guard${g.tripped ? ' tripped' : ''}`}>
              <i />
              {g.label.toUpperCase()}
            </span>
          ))
        ) : (
          <span className="guard faint">
            <i />
            NO GUARDS REPORTED
          </span>
        )}
      </div>
      <p className="receipt" style={{ marginTop: 14 }}>
        {s.last_block
          ? `last block: ${s.last_block.message} · ${s.last_block.guard} · ${hhmm(s.last_block.created_at)}`
          : 'no blocks yet — every guard holding.'}
      </p>
    </div>
  );
}

/* ---------- live runs ---------- */
function statusChip(st: Run['status']) {
  if (st === 'ok')
    return (
      <span className="chip chip-mint">
        <i />
        OK
      </span>
    );
  if (st === 'running')
    return (
      <span className="chip chip-ice live">
        <i />
        RUNNING
      </span>
    );
  return (
    <span className="chip chip-coral">
      <i />
      ERROR
    </span>
  );
}

function LiveRuns({ runs }: { runs: Run[] | null }) {
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <p className="t-label">Live Runs</p>
        <span className="chip chip-ice live">
          <i />
          POLLING · 10S
        </span>
      </div>
      {runs === null ? (
        <p className="small">Reading the wire…</p>
      ) : runs.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '26px 10px', textAlign: 'center' }}>
          <Orb agent="quinn" size="md" state="idle" />
          <p className="small">Quiet on the wire. No runs yet — the team's still warming up.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="ledger">
            <thead>
              <tr>
                <th>WORK</th>
                <th>AGENT</th>
                <th>STATUS</th>
                <th className="r">COST</th>
                <th className="r">TIME</th>
              </tr>
            </thead>
            <tbody>
              {runs.slice(0, 8).map((r) => (
                <tr key={r.id}>
                  <td title={r.pass}>{passLabel(r.pass)}</td>
                  <td>{r.agent_id ? AGENT_LABELS[r.agent_id] ?? r.agent_id : '—'}</td>
                  <td>{statusChip(r.status)}</td>
                  <td className="r">{money(r.cost_usd)}</td>
                  <td className="r">{hhmm(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- cost trajectory — real history + honest projection ----------
   Left (solid): the ACTUAL cumulative spend over the real last-14-day daily
   series (stats.series) — receipts, not decoration. Right (dashed): the
   projection forward at the backend's current pace (cost_forecast_30d / 30 per
   day), so the dashed segment's rise equals exactly the headline number. A
   founder sees what's truly been spent, then where the pace lands. */
function ForecastChart({ stats }: { stats: DashboardStats }) {
  const forecast = stats.cost_forecast_30d || 0;
  const W = 720;
  const H = 230;
  const padL = 54;
  const padR = 18;
  const padT = 22;
  const padB = 32;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const PROJ_DAYS = 30;

  const model = useMemo(() => {
    const series = stats.series ?? [];
    const nReal = series.length; // real days behind us (typically 14)
    // Cumulative actual spend across the real series (oldest→newest).
    let acc = 0;
    const realPts = series.map((d, i) => {
      acc += d.cost || 0;
      return { day: i, cum: acc };
    });
    const joinDay = nReal > 0 ? nReal - 1 : 0; // "today" — last real point
    const joinCum = acc; // actual spend to date over the window
    const pace = forecast / PROJ_DAYS; // backend's current daily pace
    // Projected cumulative forward from today at the current pace.
    const projPts = [{ day: joinDay, cum: joinCum }];
    for (let k = 1; k <= PROJ_DAYS; k++) projPts.push({ day: joinDay + k, cum: joinCum + pace * k });

    const maxDay = joinDay + PROJ_DAYS;
    const endCum = joinCum + forecast; // dashed segment lands here
    const yMax = endCum > 0 ? endCum : 1; // honest flat baseline when nothing spent
    const px = (day: number) => padL + (maxDay > 0 ? day / maxDay : 0) * plotW;
    const py = (cum: number) => padT + plotH * (1 - cum / yMax);

    const toStr = (pts: { day: number; cum: number }[]) => pts.map((p) => `${px(p.day).toFixed(1)},${py(p.cum).toFixed(1)}`).join(' ');
    const realLine = realPts.length >= 2 ? toStr(realPts) : '';
    const realArea = realPts.length >= 2 ? `${px(0).toFixed(1)},${(padT + plotH).toFixed(1)} ${realLine} ${px(joinDay).toFixed(1)},${(padT + plotH).toFixed(1)}` : '';

    return {
      realLine,
      realArea,
      projLine: toStr(projPts),
      joinX: px(joinDay),
      joinY: py(joinCum),
      endX: px(maxDay),
      endY: py(endCum),
      yMax,
      joinDay,
      maxDay,
      hasReal: realPts.length >= 2,
    };
  }, [stats.series, forecast, plotW, plotH]);

  const yticks = [0, 0.25, 0.5, 0.75, 1];
  const today = new Date();
  const dLabel = (addDays: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() + addDays);
    return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }).toUpperCase();
  };
  // x-axis ticks: window start (real), today (join), +30d projection end.
  const xticks = [
    { day: 0, label: dLabel(-model.joinDay) },
    { day: model.joinDay, label: 'TODAY' },
    { day: model.maxDay, label: dLabel(PROJ_DAYS) },
  ];
  const tickX = (day: number) => padL + (model.maxDay > 0 ? day / model.maxDay : 0) * plotW;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <p className="t-label">Cost trajectory · spend so far + projected 30-day pace</p>
          <div className="mono" style={{ fontSize: 26, fontWeight: 500, marginTop: 6, letterSpacing: '-.01em' }}>
            {money(forecast)}
          </div>
          <p className="receipt" style={{ marginTop: 4 }}>projected next 30 days at current pace</p>
        </div>
        <span className="chip chip-ice">
          <i />
          {model.hasReal ? 'REAL + PROJECTION' : 'PROJECTION'}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="Cumulative cost: real last-14-day spend plus 30-day projection at current pace">
        <defs>
          <linearGradient id="fcFade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" style={{ stopColor: 'rgba(var(--ice-rgb),.22)' }} />
            <stop offset="100%" style={{ stopColor: 'rgba(var(--ice-rgb),0)' }} />
          </linearGradient>
          <filter id="fcGlow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="4" />
          </filter>
        </defs>
        {yticks.map((t) => {
          const y = padT + plotH * (1 - t);
          return (
            <g key={t}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} style={{ stroke: 'rgba(var(--ice-rgb),.09)' }} strokeWidth={1} />
              <text x={padL - 8} y={y + 3.5} textAnchor="end" fontSize={10} style={{ fill: 'var(--faint)', fontFamily: 'var(--mono)' }}>
                {money(model.yMax * t)}
              </text>
            </g>
          );
        })}
        {/* today divider — where receipts end and projection begins */}
        <line x1={model.joinX} y1={padT} x2={model.joinX} y2={padT + plotH} strokeDasharray="3 4" style={{ stroke: 'rgba(var(--ice-rgb),.28)' }} strokeWidth={1} />
        {xticks.map((t) => (
          <text key={t.day} x={tickX(t.day)} y={H - 10} textAnchor="middle" fontSize={10} style={{ fill: 'var(--faint)', fontFamily: 'var(--mono)' }}>
            {t.label}
          </text>
        ))}
        {/* real spend so far — solid, with filled area (receipts) */}
        {model.hasReal && <polygon points={model.realArea} fill="url(#fcFade)" />}
        {model.hasReal && <polyline points={model.realLine} fill="none" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" style={{ stroke: 'var(--ice)' }} />}
        {/* projection — dashed, dimmer (not yet real) */}
        <polyline points={model.projLine} fill="none" strokeWidth={6} strokeLinecap="round" opacity={0.18} filter="url(#fcGlow)" style={{ stroke: 'var(--ice)' }} />
        <polyline points={model.projLine} fill="none" strokeWidth={2} strokeLinecap="round" strokeDasharray="5 5" opacity={0.72} style={{ stroke: 'var(--ice)' }} />
        {/* today marker */}
        <circle cx={model.joinX} cy={model.joinY} r={3.5} style={{ fill: 'var(--ice)' }} />
        {/* projection end marker */}
        <circle cx={model.endX} cy={model.endY} r={5} filter="url(#fcGlow)" style={{ fill: 'var(--ice)' }} />
        <circle cx={model.endX} cy={model.endY} r={5} style={{ fill: 'var(--ice)' }} />
        <circle cx={model.endX} cy={model.endY} r={10} fill="none" strokeWidth={1.5} style={{ stroke: 'rgba(var(--ice-rgb),.4)' }} />
      </svg>
    </div>
  );
}

/* ---------- needs your eyes — the team's open red flags ----------
   Real red[] findings from the LATEST run of each check (GET /dashboard/findings)
   — the money-relevant issues (currency bugs, missing CTA, tracking gaps) that
   otherwise hide in raw task JSON. A fixed issue disappears on the next run. */
const REAL_AGENTS: readonly AgentId[] = ['emma', 'milo', 'leo', 'quinn', 'iris', 'nora', 'grace'];
const isAgentId = (id: string): id is AgentId => (REAL_AGENTS as readonly string[]).includes(id);
const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/* The screenshot proof behind a finding: a tappable thumbnail (opens full-size).
   The agent captured this and highlighted the problem — "trust the receipts,"
   made literal. Silent if the key won't resolve or the image 404s. */
function ReceiptThumb({ keyRef }: { keyRef: string }) {
  const url = useAssetUrl(keyRef);
  const [broken, setBroken] = useState(false);
  if (!url || broken) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title="Open the full screenshot receipt"
      style={{ display: 'inline-block', marginTop: 8, textDecoration: 'none' }}
    >
      <img
        src={url}
        alt="Screenshot receipt with the issue highlighted"
        loading="lazy"
        onError={() => setBroken(true)}
        style={{ display: 'block', maxWidth: 200, maxHeight: 128, borderRadius: 8, border: '1px solid var(--line)' }}
      />
      <span className="receipt" style={{ marginTop: 4, display: 'inline-block', color: 'var(--ice)' }}>
        📎 the receipt — tap to see the proof ↗
      </span>
    </a>
  );
}

/* The exact value a founder must act on for a blocker (an IP to allow, a key name):
   a monospace chip they can tap to copy, so they don't have to retype it. Falls back
   to plain selection if the browser blocks the clipboard. */
function CopyDetail({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked — the text is still selectable in the chip */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      title="Tap to copy"
      style={{
        marginTop: 6,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 10px',
        borderRadius: 8,
        border: '1px solid var(--line-2)',
        background: 'var(--surface-2, rgba(255,255,255,.03))',
        color: 'var(--text)',
        fontFamily: 'var(--mono, ui-monospace, monospace)',
        fontSize: 13,
        cursor: 'pointer',
        userSelect: 'text',
      }}
    >
      <span>{value}</span>
      <span className="receipt" style={{ color: copied ? 'var(--mint)' : 'var(--ice)' }}>
        {copied ? 'copied ✓' : 'tap to copy'}
      </span>
    </button>
  );
}

function FindingRow({ f }: { f: TeamFinding }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', paddingBottom: 10, borderBottom: '1px solid var(--line)' }}>
      {isAgentId(f.agent_id) ? (
        <Orb agent={f.agent_id} size="xs" />
      ) : (
        <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--dim)', marginTop: 6, flexShrink: 0 }} />
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        <p className="small" style={{ color: 'var(--text)' }}>
          {f.blocked && (
            <span className="chip chip-amber" style={{ marginRight: 8, verticalAlign: 'middle' }}>
              <i />
              WAITING ON YOU
            </span>
          )}
          {/* For a blocker, lead with the plain-English "one thing to do"; the raw
              developer wording (env-var name / IP string) is kept below as proof. */}
          {f.blocked && f.plain ? f.plain : f.text}
        </p>
        {f.blocked && f.detail && <CopyDetail value={f.detail} />}
        {f.blocked && f.plain && (
          <p className="receipt faint" style={{ marginTop: 4 }}>
            exact wording: {f.text}
          </p>
        )}
        <div className="receipt" style={{ marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span>
            {cap(f.agent_id)} · {f.kind}
            {f.blocked && ' · blocked until you clear this'}
          </span>
          <span className="faint">· {ago(f.finished_at)}</span>
          {f.url && (
            <a href={f.url} target="_blank" rel="noreferrer" style={{ color: 'var(--ice)' }}>
              open page ↗
            </a>
          )}
        </div>
        {f.receipt_key && <ReceiptThumb keyRef={f.receipt_key} />}
      </div>
    </div>
  );
}

function FindingsCard({ findings }: { findings: TeamFinding[] | null }) {
  const blockedCount = findings ? findings.filter((f) => f.blocked).length : 0;
  const redCount = findings ? findings.length - blockedCount : 0;
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <p className="t-label">Needs your eyes · what the team found</p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {blockedCount > 0 && (
            <span className="chip chip-amber">
              <i />
              {blockedCount} WAITING ON YOU
            </span>
          )}
          {redCount > 0 && (
            <span className="chip chip-coral">
              <i />
              {redCount} OPEN
            </span>
          )}
        </div>
      </div>

      {findings === null ? (
        <p className="small">Gathering the team's flags…</p>
      ) : findings.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '26px 10px', textAlign: 'center' }}>
          <Orb agent="leo" size="md" state="idle" />
          <p className="small">All clear — no red flags, and nobody's blocked waiting on you.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {findings.map((f) => (
            <FindingRow key={`${f.task_id}:${f.text.slice(0, 24)}`} f={f} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- page ---------- */
export default function Dashboard() {
  const [me, setMe] = useState<Me | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [findings, setFindings] = useState<TeamFinding[] | null>(null);
  const [statsErr, setStatsErr] = useState(false);

  // one-shot: identity + stats + the team's open red flags
  useEffect(() => {
    let alive = true;
    api
      .get<Me>('/auth/me')
      .then((m) => alive && setMe(m))
      .catch(() => {});
    api
      .get<DashboardStats>('/dashboard/stats')
      .then((s) => alive && setStats(s))
      .catch(() => alive && setStatsErr(true));
    api
      .get<TeamFinding[]>('/dashboard/findings')
      .then((f) => alive && setFindings(f))
      .catch(() => alive && setFindings([]));
    return () => {
      alive = false;
    };
  }, []);

  // live runs — poll every 10s
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get<Run[]>('/dashboard/runs');
        if (alive) setRuns(r);
      } catch {
        if (alive) setRuns((prev) => prev ?? []);
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  // Real per-metric sparkline series, derived from the 14-day daily series.
  // Pass rate carries the last known rate forward on days with no QA runs
  // (honest: "no new data, rate unchanged" — not a misleading drop to zero).
  const series = stats?.series ?? [];
  const tokenSeries = useMemo(() => series.map((d) => d.tokens), [series]);
  const costSeries = useMemo(() => series.map((d) => d.cost), [series]);
  const runSeries = useMemo(() => series.map((d) => d.runs), [series]);
  const gauntSeries = useMemo(() => {
    let last = 0;
    return series.map((d) => {
      if (d.gaunt_total > 0) last = d.gaunt_ok / d.gaunt_total;
      return last;
    });
  }, [series]);

  return (
    <Layout title="Dashboard">
      <div style={{ marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontWeight: 250 }}>Welcome back, {me?.name ?? 'team'}</h1>
          <p className="small" style={{ marginTop: 4 }}>
            Calm oversight for your AI team — here's the room at a glance.
          </p>
        </div>
        <RunnerLine />
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        {/* four stat cards — sparklines plot the real last-14-day daily series */}
        <div className="grid g4">
          <Stat label="Tokens · 24h" value={stats ? nfCompact.format(stats.tokens_24h) : '—'} values={tokenSeries} />
          <Stat label="Cost · today" value={stats ? money(stats.cost_today) : '—'} values={costSeries} />
          <Stat
            label="Gauntlet pass rate"
            value={!stats ? '—' : stats.gauntlet_pass_rate === null ? '—' : pct(stats.gauntlet_pass_rate)}
            values={gauntSeries}
            sub={stats && stats.gauntlet_pass_rate === null ? 'no QA gauntlet runs yet' : undefined}
          />
          <Stat label="Runs · today" value={stats ? fmtInt(stats.runs_today) : '—'} values={runSeries} />
        </div>

        {statsErr && !stats && (
          <div className="card">
            <p className="small down">Couldn't load dashboard stats — retry by refreshing.</p>
          </div>
        )}

        {/* needs your eyes — the team's open red flags, front and centre */}
        <FindingsCard findings={findings} />

        {/* safe-mode pulse + live runs */}
        <div className="grid g2">
          {stats ? (
            <SafeMode s={stats.safe_mode} />
          ) : (
            <div className="card">
              <p className="t-label">Safe Mode</p>
              <p className="small" style={{ marginTop: 12 }}>Checking guards…</p>
            </div>
          )}
          <LiveRuns runs={runs} />
        </div>

        {/* 30-day cost forecast */}
        {stats ? (
          <ForecastChart stats={stats} />
        ) : (
          <div className="card">
            <p className="t-label">Cost forecast · projected 30-day spend</p>
            <p className="small" style={{ marginTop: 12 }}>Projecting…</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
